import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bell, Check, AlertTriangle, Info, Trash2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

const ICONS = {
  info: Info,
  warning: AlertTriangle,
  critical: ShieldAlert,
};

const TONES = {
  info: "text-info bg-info/10",
  warning: "text-warning bg-warning/10",
  critical: "text-destructive bg-destructive/10",
};

function timeAgo(iso) {
  if (!iso) return "";
  const now = Date.now();
  const then = new Date(iso).getTime();
  const s = Math.floor((now - then) / 1000);
  if (s < 60) return `${s}d lalu`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m lalu`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}j lalu`;
  const d = Math.floor(h / 24);
  return `${d}h lalu`;
}

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const isOwner = user?.role === "owner";

  // Poll unread count every 30s for owners
  const { data } = useSWR(
    isOwner ? "/notifications/unread-count" : null,
    fetcher,
    { refreshInterval: 30000 }
  );
  const { data: list } = useSWR(
    isOwner && open ? "/notifications?limit=15" : null,
    fetcher
  );

  if (!isOwner) return null;

  const count = data?.count || 0;

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/mark-read`);
    mutate("/notifications?limit=15");
    mutate("/notifications/unread-count");
  };

  const markAll = async () => {
    const res = await api.post("/notifications/mark-all-read");
    toast.success(`${res.data.updated} notifikasi ditandai dibaca`);
    mutate("/notifications?limit=15");
    mutate("/notifications/unread-count");
  };

  const remove = async (id) => {
    await api.delete(`/notifications/${id}`);
    mutate("/notifications?limit=15");
    mutate("/notifications/unread-count");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notification-bell"
          aria-label="Notifikasi"
        >
          <Bell className="h-5 w-5" />
          {count > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold grid place-items-center"
              data-testid="notification-badge"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="end" data-testid="notification-panel">
        <div className="flex items-center justify-between p-3 border-b border-border/60">
          <div>
            <div className="font-medium text-sm">Notifikasi</div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
              {count} belum dibaca
            </div>
          </div>
          {count > 0 && (
            <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={markAll} data-testid="mark-all-read">
              <Check className="h-3 w-3" /> Tandai semua
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-[420px]">
          {!list ? (
            <div className="p-6 text-sm text-muted-foreground text-center">Memuat...</div>
          ) : list.items.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">Tidak ada notifikasi.</div>
          ) : (
            <div className="divide-y divide-border/50">
              {list.items.map((n) => {
                const Icon = ICONS[n.severity] || Info;
                const unread = !n.read_at;
                return (
                  <div
                    key={n.id}
                    className={`p-3 flex gap-3 items-start ${unread ? "bg-primary/[0.03]" : ""}`}
                    data-testid={`notif-item-${n.id}`}
                  >
                    <div className={`h-8 w-8 rounded-md grid place-items-center shrink-0 ${TONES[n.severity]}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{n.title}</div>
                      <div className="text-xs text-muted-foreground line-clamp-2">{n.message}</div>
                      <div className="text-[10px] text-muted-foreground mt-1">{timeAgo(n.created_at)}</div>
                    </div>
                    <div className="flex flex-col gap-1">
                      {unread && (
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => markRead(n.id)} title="Tandai dibaca" data-testid={`mark-read-${n.id}`}>
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => remove(n.id)} title="Hapus">
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
